import { lazy } from 'react';
import { CalendarDays, ClipboardList, Coins, FileCog, HandCoins, Landmark, ListChecks, Scale } from 'lucide-react';
import type { AppRoute } from '@/app/registry';
import { CP } from '@/api/commission';
import { FEATURE as F } from '@/lib/features';

/**
 * Commission (Hoa hồng) routes, spread into APP_ROUTES by src/app/registry.tsx.
 * Permissions mirror CommissionEndpoints.cs exactly (any-of).
 * Features: reading policies is core; creating / editing versions is `commission.policy_editor`. Payout runs, schedules,
 * the payout calendar and holidays all belong to `commission.payouts`.
 */
const own = [CP.accrualReadOwn, CP.accrualReadAll];
const scheduleReaders = [CP.scheduleManage, CP.payoutManage, CP.accrualReadAll];

export const commissionRoutes: AppRoute[] = [
  {
    path: 'commission',
    title: 'Hoa hồng của tôi',
    perms: own,
    page: lazy(() => import('./me/MyCommissionPage')),
    nav: { group: 'commission', label: 'Hoa hồng của tôi', icon: Coins, end: true },
  },
  {
    path: 'commission/desk',
    title: 'Bàn hoa hồng',
    perms: [CP.accrualReadAll],
    page: lazy(() => import('./desk/DeskPage')),
    nav: { group: 'commission', label: 'Bàn hoa hồng', icon: ClipboardList },
  },
  {
    path: 'commission/accruals',
    title: 'Khoản hoa hồng',
    perms: own,
    page: lazy(() => import('./accruals/AccrualsPage')),
    nav: { group: 'commission', label: 'Khoản hoa hồng', icon: ListChecks },
  },
  { path: 'commission/accruals/:id', title: 'Chi tiết khoản hoa hồng', perms: own, page: lazy(() => import('./accruals/AccrualDetailPage')) },
  {
    path: 'commission/adjustments',
    feature: F.commissionAdjustments,
    title: 'Điều chỉnh hoa hồng',
    perms: [...own, CP.adjustmentRequest],
    page: lazy(() => import('./adjustments/AdjustmentsPage')),
    nav: { group: 'commission', label: 'Điều chỉnh', icon: Scale },
  },
  {
    path: 'commission/payouts',
    feature: F.commissionPayouts,
    title: 'Chi hoa hồng',
    perms: own,
    page: lazy(() => import('./payouts/PayoutsPage')),
    nav: { group: 'commission', label: 'Phiếu & đợt chi', icon: HandCoins },
  },
  { path: 'commission/payouts/:id', feature: F.commissionPayouts, title: 'Phiếu chi hoa hồng', perms: own, page: lazy(() => import('./payouts/PayoutDetailPage')) },
  {
    path: 'commission/calendar',
    feature: F.commissionPayouts,
    title: 'Lịch chi hoa hồng',
    perms: own,
    page: lazy(() => import('./calendar/CalendarPage')),
    nav: { group: 'commission', label: 'Lịch chi', icon: CalendarDays },
  },
  {
    path: 'commission/schedules',
    feature: F.commissionPayouts,
    title: 'Cấu hình lịch chi',
    perms: scheduleReaders,
    page: lazy(() => import('./schedules/SchedulesPage')),
    nav: { group: 'commission', label: 'Cấu hình lịch chi', icon: Landmark },
  },
  { path: 'commission/schedules/new', feature: F.commissionPayouts, title: 'Tạo lịch chi', perms: [CP.scheduleManage], page: lazy(() => import('./schedules/ScheduleEditorPage')) },
  { path: 'commission/schedules/:id', feature: F.commissionPayouts, title: 'Lịch chi hoa hồng', perms: scheduleReaders, page: lazy(() => import('./schedules/ScheduleDetailPage')) },
  { path: 'commission/holidays', feature: F.commissionPayouts, title: 'Ngày nghỉ (lịch chi)', perms: scheduleReaders, page: lazy(() => import('./schedules/HolidaysPage')) },
  {
    path: 'commission/policies',
    title: 'Chính sách hoa hồng',
    perms: [CP.policyRead],
    page: lazy(() => import('./policies/PoliciesPage')),
    nav: { group: 'commission', label: 'Chính sách', icon: FileCog },
  },
  { path: 'commission/policies/new', feature: F.commissionPolicyEditor, title: 'Tạo chính sách hoa hồng', perms: [CP.policyManage], page: lazy(() => import('./policies/PolicyEditorPage')) },
  { path: 'commission/policies/:id', title: 'Chính sách hoa hồng', perms: [CP.policyRead], page: lazy(() => import('./policies/PolicyDetailPage')) },
  {
    path: 'commission/policies/:id/versions/:vid',
    feature: F.commissionPolicyEditor,
    title: 'Phiên bản chính sách',
    perms: [CP.policyRead],
    page: lazy(() => import('./policies/PolicyEditorPage')),
  },
];
